import {useEffect, useId, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {
  FaChevronDown,
  FaChevronUp,
  FaTimes,
  FaVolumeMute,
  FaBolt,
  FaVolumeUp,
} from 'react-icons/fa';
import styled, {css, keyframes} from 'styled-components';
import {VisuallyHidden} from '../a11y/VisuallyHidden';
import {
  CHORD_SECONDS,
  chartMaxK,
  dropsIn,
  FIRST_CHORD,
  histogram,
  nextChord,
  PATCHES,
  patchOf,
  patchRate,
  RAIN_TONES,
  ROUND_SECONDS,
  toneNote,
  TOTAL_RATE,
  waitForNextDrop,
  type ChordName,
  type RainTone,
  type SliceCounts,
} from './rainModel';
import {RainCafe} from './RainCafe';
import {RainClouds, type CloudBase} from './RainClouds';
import {startRainAudio, type RainAudio} from './rainSounds';
import {readRainVolume, writeRainVolume, type RainVolume} from './rainVolume';

/**
 * Poisson rain: clouds roll in over the whole page and rain falls, each
 * drop a note of its own, landing anywhere along the ground. A card counts
 * the drops in slices, either seconds of time or patches of ground, and
 * charts the counts beside the Poisson distribution they follow. The rain
 * layer never takes a click; only the card and the close button do.
 */

/** How long a drop takes to fall from the clouds to the ground, in ms. */
const FALL_MS = 1300;

/** Where drops start before the sky is painted, in px from the top. */
const DROP_TOP = 56;

/** How far inside a cloud's base a drop is born, so it emerges from it. */
const INSIDE_CLOUD = 16;

/** The ground's height at the bottom of the screen, in px. */
const GROUND = 40;

/** How far the wind carries a drop sideways on its way down, in px. */
const WIND = 22;

/** A falling drop's radius, in px. */
const DROP_SIZE = 7;

/** How long the clouds take to roll in, in ms. */
const CLOUDS_IN_MS = 2200;

/** How long after the press the first drop may leave the clouds, in ms. */
const FIRST_DROP_MS = 1500;

/** How long the clouds take to roll away after the close button, in ms. */
const LEAVE_MS = 900;

/** How often birds sing, per second, on average. */
const BIRD_RATE = 0.12;

/**
 * How far ahead notes are booked on the audio clock in a background tab, in
 * ms. A background tab runs timers about once a second and draws no frames,
 * so this keeps the notes playing there.
 */
const HIDDEN_LOOKAHEAD_MS = 2500;

/**
 * How far ahead notes are booked while the tab is in view, in ms: short, so
 * a change to a note's θ is heard at once.
 */
const VISIBLE_LOOKAHEAD_MS = 200;

/** The highest θ a note's slider reaches, in drops a second. */
const MAX_NOTE_RATE = 4;

/** The highest θ the slider for all the notes together reaches. */
const MAX_TOTAL_RATE = 12;

/** How often lightning strikes to begin with, per minute, on average. */
const DEFAULT_STRIKES_PER_MINUTE = 3;

/** The highest θ the lightning slider reaches, in strikes a minute. */
const MAX_STRIKES_PER_MINUTE = 30;

/** How long one lightning flash lasts, flickers and all, in ms. */
const FLASH_MS = 300;

/** How often the rain steps on a timer, for when no frames are drawn. */
const STEP_MS = 250;

/** A longer gap between steps (a sleeping laptop) starts the count afresh. */
const RESET_GAP_MS = 10000;

/** The most notes that may start together, so a burst stays pleasant. */
const MAX_TOGETHER = 3;

/** How close together two notes count as together, in ms. */
const TOGETHER_MS = 50;

/** How long a splash shows, in ms. */
const SPLASH_MS = 800;

/** Where the chart's plot sits in its 300-wide view box. */
const PLOT = {left: 22, right: 294, top: 6, base: 98};

/** How the card slices the rain: by seconds of time or patches of ground. */
type SliceMode = 'time' | 'space';

/** A drop on its way down, or booked to leave the clouds soon. */
interface Drop {
  tone: RainTone;
  /** Where it lands, as a fraction of the screen's width in [0, 1). */
  x: number;
  /** Where it starts, in px from the top: inside the cloud above it. */
  top: number;
  /** When it leaves the clouds, in performance.now() milliseconds. */
  spawnMs: number;
}

/** A drop that has landed, splashing for a moment. */
interface Splash {
  color: string;
  x: number;
  landMs: number;
}

/**
 * A lightning bolt, as points from the cloud base down to the ground: each
 * point is [sideways offset in px, how far down in (0, 1]].
 */
interface Bolt {
  trunk: (readonly [number, number])[];
  branches: (readonly [number, number])[][];
}

/** A lightning strike; its thunder follows after the sound's travel time. */
interface Flash {
  atMs: number;
  /** How far off it is, in [0, 1]; a far one only lights up the clouds. */
  distance: number;
  /** Where it strikes, as a fraction of the screen's width. */
  x: number;
  bolt: Bolt | undefined;
}

/** A drop resting on its patch of ground until the round is tallied. */
interface GroundDot {
  round: number;
  patch: number;
  tone: RainTone;
}

/** The slices still filling, for the card's strip. */
interface LiveSlices {
  /** The second being counted now. */
  second: SliceCounts;
  /** Each patch of ground this round, left to right. */
  patches: SliceCounts[];
}

/** The running simulation, kept outside React state. */
interface Shower {
  drops: Drop[];
  splashes: Splash[];
  groundDots: GroundDot[];
  /** When each note's next drop leaves the clouds. */
  nextSpawnMs: number[];
  nextBirdMs: number;
  nextThunderMs: number;
  flashes: Flash[];
  /** When the notes already booked will sound, to keep bursts small. */
  bookedNotesMs: number[];
  /**
   * The song's chords from the one sounding now, each lasting
   * `CHORD_SECONDS`; written further ahead as notes are booked.
   */
  song: {startMs: number; chord: ChordName}[];
  /** When the first counted second began. */
  startMs: number;
  /** The second being counted now; earlier ones are on the chart. */
  nextSecond: number;
  /** Counts for seconds not yet on the chart, by second index. */
  pending: Map<number, Record<string, number>>;
  /** Counts for each patch in rounds not yet tallied, by round index. */
  pendingPatches: Map<number, Record<string, number>[]>;
  lastStepMs: number;
}

/** Each note's θ, in drops a second, by tone id. */
type Rates = Readonly<Record<string, number>>;

/** When a note falling at `rate` next leaves the clouds after `ms`. */
function nextDropAfter({ms, rate}: {ms: number; rate: number}): number {
  if (rate <= 0) {
    return Infinity;
  }
  return ms + 1000 * waitForNextDrop({rate, random: Math.random});
}

function newShower({
  nowMs,
  delayMs,
  rates,
}: {
  nowMs: number;
  delayMs: number;
  rates: Rates;
}): Shower {
  const firstMs = nowMs + delayMs;
  return {
    drops: [],
    splashes: [],
    groundDots: [],
    nextSpawnMs: RAIN_TONES.map(tone =>
      nextDropAfter({ms: firstMs, rate: rates[tone.id] ?? tone.rate}),
    ),
    nextBirdMs: nextDropAfter({ms: firstMs, rate: BIRD_RATE}),
    // The storm opens with a roll of thunder as the clouds arrive.
    nextThunderMs: nowMs + 400 + Math.random() * 1800,
    flashes: [],
    bookedNotesMs: [],
    song: [{startMs: firstMs + FALL_MS, chord: FIRST_CHORD}],
    startMs: firstMs + FALL_MS,
    nextSecond: 0,
    pending: new Map(),
    pendingPatches: new Map(),
    lastStepMs: nowMs,
  };
}

/**
 * Starts the count afresh from drops that leave the clouds after `nowMs`,
 * as when a note's θ changes; the drops already falling still land.
 */
function restartCount({shower, nowMs}: {shower: Shower; nowMs: number}) {
  shower.startMs = nowMs + FALL_MS;
  shower.nextSecond = 0;
  shower.pending = new Map();
  shower.pendingPatches = new Map();
  shower.groundDots = [];
}

/**
 * The chord sounding at `ms`, writing the song further ahead as needed.
 * Before the song starts, it is the first chord.
 */
function chordAt({shower, ms}: {shower: Shower; ms: number}): ChordName {
  const {song} = shower;
  let last = song[song.length - 1];
  while (last && ms >= last.startMs + CHORD_SECONDS * 1000) {
    last = {
      startMs: last.startMs + CHORD_SECONDS * 1000,
      chord: nextChord({chord: last.chord, random: Math.random}),
    };
    song.push(last);
  }
  let sounding = song[0];
  for (const entry of song) {
    if (entry.startMs <= ms) {
      sounding = entry;
    }
  }
  return sounding?.chord ?? FIRST_CHORD;
}

/** A jagged bolt with a branch or two. */
function makeBolt(random: () => number): Bolt {
  const trunk: (readonly [number, number])[] = [[0, 0]];
  let x = 0;
  let y = 0;
  while (y < 1) {
    y = Math.min(1, y + 0.05 + random() * 0.07);
    x += (random() - 0.5) * 34;
    trunk.push([x, y]);
  }
  const branches: (readonly [number, number])[][] = [];
  const count = 1 + Math.floor(random() * 2);
  for (let index = 0; index < count; index += 1) {
    const from =
      trunk[1 + Math.floor(random() * trunk.length * 0.5)] ?? trunk[0];
    let [branchX, branchY] = from ?? [0, 0];
    const side = random() < 0.5 ? -1 : 1;
    const branch: (readonly [number, number])[] = [[branchX, branchY]];
    const length = 3 + Math.floor(random() * 4);
    for (let point = 0; point < length; point += 1) {
      branchY += 0.03 + random() * 0.04;
      branchX += side * (6 + random() * 14);
      branch.push([branchX, branchY]);
    }
    branches.push(branch);
  }
  return {trunk, branches};
}

/**
 * How bright a flash is `ms` after it strikes: two flickers, the second
 * fading. Two flashes, well under three a second, are safe for viewers
 * sensitive to flashing.
 */
function flicker(ms: number): number {
  if (ms < 0 || ms >= FLASH_MS) {
    return 0;
  }
  if (ms < 70) {
    return 1;
  }
  if (ms < 140) {
    return 0.12;
  }
  return 0.75 * (1 - (ms - 140) / (FLASH_MS - 140));
}

/** How brightly lightning lights the clouds now, in [0, 1]. */
function flashLevel({shower, nowMs}: {shower: Shower; nowMs: number}): number {
  return shower.flashes.reduce(
    (level, flash) =>
      Math.max(
        level,
        flicker(nowMs - flash.atMs) * (flash.bolt === undefined ? 0.55 : 1),
      ),
    0,
  );
}

function emptyPatches(): Record<string, number>[] {
  return Array.from({length: PATCHES}, () => ({}));
}

function liveSlices(shower: Shower): LiveSlices {
  const current = shower.nextSecond;
  const round = Math.floor(current / ROUND_SECONDS);
  return {
    second: {...shower.pending.get(current)},
    patches: (shower.pendingPatches.get(round) ?? emptyPatches()).map(
      patch => ({...patch}),
    ),
  };
}

/**
 * Moves the rain on to `nowMs`: books drops (and their notes), birdsong and
 * lightning up to `lookaheadMs` ahead, lands the drops that are due, and
 * finishes the seconds and rounds that are over.
 */
function step({
  shower,
  nowMs,
  audio,
  rates,
  lookaheadMs,
  cloudBase,
  thunderRate,
}: {
  shower: Shower;
  nowMs: number;
  audio: RainAudio;
  rates: Rates;
  lookaheadMs: number;
  cloudBase: CloudBase | null;
  /** Lightning strikes per second. */
  thunderRate: number;
}): {seconds: SliceCounts[]; patches: SliceCounts[]; isChanged: boolean} {
  const bornAt = (x: number) => {
    if (!cloudBase || typeof window === 'undefined') {
      return DROP_TOP;
    }
    const startX = (x * window.innerWidth - WIND) / window.innerWidth;
    return cloudBase(startX) - INSIDE_CLOUD;
  };
  const horizonMs = nowMs + lookaheadMs;
  shower.bookedNotesMs = shower.bookedNotesMs.filter(
    noteMs => noteMs > nowMs - TOGETHER_MS,
  );
  while ((shower.song[1]?.startMs ?? Infinity) <= nowMs) {
    shower.song.shift();
  }
  RAIN_TONES.forEach((tone, index) => {
    let next = shower.nextSpawnMs[index] ?? nowMs;
    // Booked by when it leaves the cloud, so it is drawn for its whole fall;
    // its note is booked for when it lands.
    while (next <= horizonMs) {
      const landMs = next + FALL_MS;
      const x = Math.random();
      shower.drops.push({tone, x, top: bornAt(x), spawnMs: next});
      const together = shower.bookedNotesMs.filter(
        noteMs => Math.abs(noteMs - landMs) < TOGETHER_MS,
      ).length;
      if (landMs >= nowMs && together < MAX_TOGETHER) {
        audio.playDrop({
          frequency: toneNote({tone, chord: chordAt({shower, ms: landMs})})
            .frequency,
          delaySec: (landMs - nowMs) / 1000,
        });
        shower.bookedNotesMs.push(landMs);
      }
      next = nextDropAfter({ms: next, rate: rates[tone.id] ?? tone.rate});
    }
    shower.nextSpawnMs[index] = next;
  });
  while (shower.nextBirdMs <= horizonMs) {
    if (shower.nextBirdMs >= nowMs) {
      audio.playBird({delaySec: (shower.nextBirdMs - nowMs) / 1000});
    }
    shower.nextBirdMs = nextDropAfter({
      ms: shower.nextBirdMs,
      rate: BIRD_RATE,
    });
  }
  while (shower.nextThunderMs <= horizonMs) {
    const atMs = shower.nextThunderMs;
    if (atMs >= nowMs) {
      const distance = Math.random();
      shower.flashes.push({
        atMs,
        distance,
        x: 0.1 + 0.8 * Math.random(),
        bolt: distance < 0.5 ? makeBolt(Math.random) : undefined,
      });
      // Sound is slower than light: about three seconds a kilometre.
      audio.playThunder({
        distance,
        delaySec: (atMs - nowMs) / 1000 + 0.3 + 2.7 * distance,
      });
    }
    shower.nextThunderMs = nextDropAfter({ms: atMs, rate: thunderRate});
  }
  shower.flashes = shower.flashes.filter(
    flash => nowMs - flash.atMs < FLASH_MS,
  );

  let isChanged = false;
  shower.drops = shower.drops.filter(drop => {
    const landMs = drop.spawnMs + FALL_MS;
    if (landMs > nowMs) {
      return true;
    }
    const second = Math.floor((landMs - shower.startMs) / 1000);
    if (second >= shower.nextSecond) {
      const {id} = drop.tone;
      const counts = shower.pending.get(second) ?? {};
      counts[id] = (counts[id] ?? 0) + 1;
      shower.pending.set(second, counts);
      const round = Math.floor(second / ROUND_SECONDS);
      const patch = patchOf(drop.x);
      const patches = shower.pendingPatches.get(round) ?? emptyPatches();
      const onPatch = patches[patch] ?? {};
      onPatch[id] = (onPatch[id] ?? 0) + 1;
      patches[patch] = onPatch;
      shower.pendingPatches.set(round, patches);
      shower.groundDots.push({round, patch, tone: drop.tone});
      isChanged = true;
    }
    if (nowMs - landMs < SPLASH_MS) {
      shower.splashes.push({color: drop.tone.color, x: drop.x, landMs});
    }
    return false;
  });
  shower.splashes = shower.splashes.filter(
    splash => nowMs - splash.landMs < SPLASH_MS,
  );

  const seconds: SliceCounts[] = [];
  const patches: SliceCounts[] = [];
  while (shower.startMs + (shower.nextSecond + 1) * 1000 <= nowMs) {
    seconds.push(shower.pending.get(shower.nextSecond) ?? {});
    shower.pending.delete(shower.nextSecond);
    if ((shower.nextSecond + 1) % ROUND_SECONDS === 0) {
      const round = Math.floor(shower.nextSecond / ROUND_SECONDS);
      patches.push(...(shower.pendingPatches.get(round) ?? emptyPatches()));
      shower.pendingPatches.delete(round);
      shower.groundDots = shower.groundDots.filter(dot => dot.round > round);
    }
    shower.nextSecond += 1;
    isChanged = true;
  }
  shower.lastStepMs = nowMs;
  return {seconds, patches, isChanged};
}

/** Whether the viewer asked for less motion. */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** A rate for reading: at most two decimals, no trailing zeros. */
function formatRate(rate: number): string {
  return String(Number(rate.toFixed(2)));
}

/** Draws one teardrop, its point up, its round bottom at (x, y). */
function teardrop({
  context,
  x,
  y,
  size,
  color,
}: {
  context: CanvasRenderingContext2D;
  x: number;
  y: number;
  size: number;
  color: string;
}) {
  context.fillStyle = color;
  context.beginPath();
  context.moveTo(x, y - size * 2.4);
  context.bezierCurveTo(
    x + size * 0.35,
    y - size * 1.3,
    x + size,
    y - size * 0.6,
    x + size,
    y,
  );
  context.arc(x, y, size, 0, Math.PI);
  context.bezierCurveTo(
    x - size,
    y - size * 0.6,
    x - size * 0.35,
    y - size * 1.3,
    x,
    y - size * 2.4,
  );
  context.fill();
  context.fillStyle = '#ffffff';
  context.globalAlpha *= 0.85;
  context.beginPath();
  context.ellipse(
    x - size * 0.38,
    y - size * 0.3,
    size * 0.22,
    size * 0.38,
    -0.4,
    0,
    Math.PI * 2,
  );
  context.fill();
}

/**
 * Draws the ground along the bottom. Counting by space, it is cut into
 * patches, and each drop this round rests on its patch as a dot.
 */
function drawGround({
  context,
  shower,
  width,
  height,
  mode,
  toneId,
}: {
  context: CanvasRenderingContext2D;
  shower: Shower;
  width: number;
  height: number;
  mode: SliceMode;
  toneId: string | undefined;
}) {
  const ground = height - GROUND;
  context.globalAlpha = 1;
  const water = context.createLinearGradient(0, ground, 0, height);
  water.addColorStop(0, 'rgba(62, 124, 177, 0.12)');
  water.addColorStop(1, 'rgba(62, 124, 177, 0.26)');
  context.fillStyle = water;
  context.fillRect(0, ground, width, GROUND);
  context.strokeStyle = 'rgba(62, 124, 177, 0.5)';
  context.lineWidth = 1.5;
  context.beginPath();
  context.moveTo(0, ground);
  context.lineTo(width, ground);
  context.stroke();
  if (mode !== 'space') {
    return;
  }

  const patchWidth = width / PATCHES;
  for (let patch = 0; patch < PATCHES; patch += 1) {
    if (patch % 2 === 1) {
      context.fillStyle = 'rgba(255, 255, 255, 0.4)';
      context.fillRect(patch * patchWidth, ground, patchWidth, GROUND);
    }
    if (patch > 0) {
      context.strokeStyle = 'rgba(28, 25, 23, 0.45)';
      context.lineWidth = 1.5;
      context.beginPath();
      context.moveTo(patch * patchWidth, ground - 8);
      context.lineTo(patch * patchWidth, height);
      context.stroke();
    }
  }
  const dot = 7;
  const gap = 2;
  const columns = Math.max(1, Math.floor((patchWidth - gap) / (dot + gap)));
  const piled = new Array<number>(PATCHES).fill(0);
  for (const resting of shower.groundDots) {
    const index = piled[resting.patch] ?? 0;
    piled[resting.patch] = index + 1;
    const left =
      resting.patch * patchWidth +
      (patchWidth - columns * (dot + gap) + gap) / 2;
    const column = index % columns;
    const row = Math.floor(index / columns);
    context.globalAlpha =
      toneId === undefined || resting.tone.id === toneId ? 1 : 0.25;
    context.fillStyle = resting.tone.color;
    context.beginPath();
    context.arc(
      left + column * (dot + gap) + dot / 2,
      height - 6 - row * (dot + gap) - dot / 2,
      dot / 2,
      0,
      Math.PI * 2,
    );
    context.fill();
  }
  context.globalAlpha = 1;
}

/**
 * Draws lightning: each near strike's bolt, from under the clouds to the
 * ground, while it flickers. The clouds light up separately.
 */
function drawLightning({
  context,
  shower,
  nowMs,
  width,
  height,
  cloudBase,
}: {
  context: CanvasRenderingContext2D;
  shower: Shower;
  nowMs: number;
  width: number;
  height: number;
  cloudBase: CloudBase | null;
}) {
  const ground = height - GROUND;
  for (const flash of shower.flashes) {
    const top = cloudBase ? cloudBase(flash.x) - 6 : DROP_TOP + 30;
    const level = flicker(nowMs - flash.atMs);
    if (!flash.bolt || level <= 0) {
      continue;
    }
    const x = flash.x * width;
    const toScreen = ([dx, down]: readonly [number, number]) =>
      [x + dx, top + down * (ground - top)] as const;
    const stroke = (points: readonly (readonly [number, number])[]) => {
      context.beginPath();
      points.map(toScreen).forEach(([px, py], index) => {
        if (index === 0) {
          context.moveTo(px, py);
        } else {
          context.lineTo(px, py);
        }
      });
      context.stroke();
    };
    context.save();
    context.globalAlpha = level;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.shadowColor = 'rgba(120, 140, 255, 1)';
    context.shadowBlur = 28;
    context.strokeStyle = 'rgba(150, 168, 255, 0.7)';
    context.lineWidth = 9;
    stroke(flash.bolt.trunk);
    context.strokeStyle = '#ffffff';
    context.lineWidth = 3.2;
    stroke(flash.bolt.trunk);
    context.lineWidth = 1.3;
    for (const branch of flash.bolt.branches) {
      stroke(branch);
    }
    const end = flash.bolt.trunk[flash.bolt.trunk.length - 1] ?? [0, 1];
    const [landX] = toScreen(end);
    const glow = context.createRadialGradient(
      landX,
      ground,
      0,
      landX,
      ground,
      90,
    );
    glow.addColorStop(0, 'rgba(220, 230, 255, 0.7)');
    glow.addColorStop(1, 'rgba(220, 230, 255, 0)');
    context.shadowBlur = 0;
    context.fillStyle = glow;
    context.fillRect(landX - 90, ground - 90, 180, 180);
    context.restore();
  }
}

/** Draws the falling drops and the splashes. */
function drawRain({
  context,
  shower,
  nowMs,
  width,
  height,
}: {
  context: CanvasRenderingContext2D;
  shower: Shower;
  nowMs: number;
  width: number;
  height: number;
}) {
  const ground = height - GROUND;
  for (const drop of shower.drops) {
    const progress = (nowMs - drop.spawnMs) / FALL_MS;
    if (progress < 0) {
      continue;
    }
    // A touch of acceleration, and a slant with the wind.
    const eased = Math.min(1, progress) ** 1.25;
    const x = drop.x * width - WIND * (1 - eased);
    const y = drop.top + eased * (ground - drop.top);
    const streak = 26 + 44 * eased;
    const gradient = context.createLinearGradient(x - 6, y - streak, x, y);
    gradient.addColorStop(0, 'rgba(255, 255, 255, 0)');
    gradient.addColorStop(1, drop.tone.color);
    context.globalAlpha = 0.5;
    context.strokeStyle = gradient;
    context.lineWidth = 3.5;
    context.lineCap = 'round';
    context.beginPath();
    context.moveTo(x - 6, y - streak);
    context.lineTo(x, y - DROP_SIZE * 1.6);
    context.stroke();
    context.globalAlpha = 0.95;
    teardrop({context, x, y, size: DROP_SIZE, color: drop.tone.color});
  }
  for (const splash of shower.splashes) {
    const age = (nowMs - splash.landMs) / SPLASH_MS;
    const x = splash.x * width;
    const fade = Math.max(0, 1 - age);
    context.globalAlpha = 0.9 * fade;
    context.strokeStyle = splash.color;
    context.lineWidth = 2.5;
    context.beginPath();
    context.ellipse(x, ground, 8 + 40 * age, 3 + 8 * age, 0, 0, Math.PI * 2);
    context.stroke();
    context.globalAlpha = 0.5 * fade;
    context.beginPath();
    context.ellipse(x, ground, 4 + 22 * age, 1.5 + 4 * age, 0, 0, Math.PI * 2);
    context.stroke();
    context.globalAlpha = 0.9 * fade;
    context.fillStyle = splash.color;
    for (const [spread, lift] of [
      [-26, 34],
      [-12, 50],
      [0, 58],
      [12, 48],
      [26, 32],
    ] as const) {
      context.beginPath();
      context.arc(
        x + spread * age,
        ground - 4 - lift * age + 70 * age * age,
        2.8,
        0,
        Math.PI * 2,
      );
      context.fill();
    }
    // The note this drop plays, rising from where it landed.
    context.font = '600 22px system-ui, sans-serif';
    context.textAlign = 'center';
    context.fillText('♪', x + 12 * age, ground - 34 - 46 * age);
  }
  context.globalAlpha = 1;
}

/**
 * Rain over the whole page, with its card. Mount it to start the rain; its
 * close button, Escape, or `isStopping` rolls the clouds away and then calls
 * `onClose`.
 *
 * @param props.onClose - Called once the clouds have rolled away.
 * @param props.isStopping - True to roll the clouds away, as when the rain
 *     cloud that started the rain is pressed again. Omitted means the rain
 *     falls until its own close button or Escape.
 * @return The rain, portaled to the document body.
 * @throws Never.
 */
export function PoissonRain({
  onClose,
  isStopping = false,
}: {
  onClose: () => void;
  isStopping?: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const audioRef = useRef<RainAudio | null>(null);
  const [seconds, setSeconds] = useState<SliceCounts[]>([]);
  const [patchSlices, setPatchSlices] = useState<SliceCounts[]>([]);
  const [live, setLive] = useState<LiveSlices>(() => ({
    second: {},
    patches: emptyPatches(),
  }));
  const [mode, setMode] = useState<SliceMode>('time');
  const [toneId, setToneId] = useState<string | undefined>();
  const viewRef = useRef({mode, toneId});
  const [rates, setRates] = useState<Rates>(() =>
    Object.fromEntries(RAIN_TONES.map(tone => [tone.id, tone.rate])),
  );
  const ratesRef = useRef(rates);
  const [strikesPerMinute, setStrikesPerMinute] = useState(
    DEFAULT_STRIKES_PER_MINUTE,
  );
  const strikesRef = useRef(strikesPerMinute);
  const [isLightningKnob, setIsLightningKnob] = useState(false);
  const showerRef = useRef<Shower | null>(null);
  const skyRef = useRef<HTMLDivElement>(null);
  const cloudBaseRef = useRef<CloudBase | null>(null);
  const [chord, setChord] = useState<ChordName>(FIRST_CHORD);
  const [volume, setVolume] = useState<RainVolume>(() => readRainVolume());
  const volumeRef = useRef(volume);
  const [isVolumeOpen, setIsVolumeOpen] = useState(false);
  const volumeId = useId();
  const rainVolumeId = useId();
  const dropsVolumeId = useId();
  const [isFolded, setIsFolded] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const isLeaving = isClosing || isStopping;
  const chartId = useId();
  const sliderId = useId();
  const [announcement, setAnnouncement] = useState('');
  const isStill = useMemo(prefersReducedMotion, []);
  const titleId = useId();

  useEffect(() => {
    viewRef.current = {mode, toneId};
  }, [mode, toneId]);

  useEffect(() => {
    closeRef.current?.focus();
    // Set after the live region is on the page, so it is announced.
    const greeting = window.setTimeout(() => {
      setAnnouncement(
        'It is raining. Six voices fall at their own rates, each playing its note of a chord that moves every few seconds, and a card counts the drops in each second, or on each patch of ground. Press Escape or the close button to stop.',
      );
    }, 100);
    const audio = startRainAudio({volume: volumeRef.current});
    audioRef.current = audio;
    let shower = newShower({
      nowMs: performance.now(),
      delayMs: isStill ? 0 : FIRST_DROP_MS,
      rates: ratesRef.current,
    });
    showerRef.current = shower;
    let shownChord = FIRST_CHORD;
    const advance = () => {
      const nowMs = performance.now();
      if (nowMs - shower.lastStepMs > RESET_GAP_MS) {
        shower = newShower({nowMs, delayMs: 0, rates: ratesRef.current});
        showerRef.current = shower;
        setLive(liveSlices(shower));
      }
      const result = step({
        shower,
        nowMs,
        audio,
        rates: ratesRef.current,
        lookaheadMs: document.hidden
          ? HIDDEN_LOOKAHEAD_MS
          : VISIBLE_LOOKAHEAD_MS,
        cloudBase: cloudBaseRef.current,
        thunderRate: strikesRef.current / 60,
      });
      if (result.seconds.length > 0) {
        setSeconds(previous => [...previous, ...result.seconds]);
      }
      if (result.patches.length > 0) {
        setPatchSlices(previous => [...previous, ...result.patches]);
      }
      const sounding = chordAt({shower, ms: nowMs});
      if (sounding !== shownChord) {
        shownChord = sounding;
        setChord(sounding);
      }
      if (result.isChanged) {
        setLive(liveSlices(shower));
      }
      return nowMs;
    };
    const timer = window.setInterval(advance, STEP_MS);
    // Book further ahead the moment the tab is hidden, before timers slow.
    document.addEventListener('visibilitychange', advance);
    let frame = 0;
    let skyFilter = '';
    const draw = () => {
      const nowMs = advance();
      const canvas = canvasRef.current;
      const context = canvas?.getContext('2d');
      if (canvas && context) {
        const ratio = window.devicePixelRatio || 1;
        const width = window.innerWidth;
        const height = window.innerHeight;
        if (
          canvas.width !== width * ratio ||
          canvas.height !== height * ratio
        ) {
          canvas.width = width * ratio;
          canvas.height = height * ratio;
        }
        context.setTransform(ratio, 0, 0, ratio, 0, 0);
        context.clearRect(0, 0, width, height);
        drawGround({context, shower, width, height, ...viewRef.current});
        if (!isStill) {
          drawLightning({
            context,
            shower,
            nowMs,
            width,
            height,
            cloudBase: cloudBaseRef.current,
          });
          drawRain({context, shower, nowMs, width, height});
        }
      }
      const level = isStill ? 0 : flashLevel({shower, nowMs});
      const filter =
        level > 0 ? `brightness(${(1 + 0.6 * level).toFixed(2)})` : '';
      if (skyRef.current && filter !== skyFilter) {
        skyRef.current.style.filter = filter;
        skyFilter = filter;
      }
      frame = requestAnimationFrame(draw);
    };
    if (typeof requestAnimationFrame !== 'undefined') {
      frame = requestAnimationFrame(draw);
    }
    return () => {
      window.clearTimeout(greeting);
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', advance);
      showerRef.current = null;
      if (typeof cancelAnimationFrame !== 'undefined') {
        cancelAnimationFrame(frame);
      }
      audio.stop();
      audioRef.current = null;
    };
  }, [isStill]);

  useEffect(() => {
    if (!isLeaving) {
      return;
    }
    audioRef.current?.stop();
    const done = window.setTimeout(onClose, isStill ? 0 : LEAVE_MS);
    return () => window.clearTimeout(done);
  }, [isLeaving, isStill, onClose]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsClosing(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const tone = RAIN_TONES.find(each => each.id === toneId);
  const totalRate = RAIN_TONES.reduce(
    (sum, each) => sum + (rates[each.id] ?? 0),
    0,
  );
  const perSecond = tone ? (rates[tone.id] ?? 0) : totalRate;
  const isSpace = mode === 'space';
  const lambda = isSpace ? patchRate(perSecond) : perSecond;
  const slices = isSpace ? patchSlices : seconds;
  const maxK = chartMaxK(lambda);
  const bins = histogram({slices, rate: lambda, maxK, toneId});
  const tallest = Math.max(
    0.0001,
    ...bins.map(bin => Math.max(bin.observed, bin.expected)),
  );
  const barColor = tone?.color ?? '#5A6FD1';
  // Each box is keyed by the patch, or by the second it counts (the last
  // box is the second now being counted; negative ones are not yet here).
  const cells: {key: string; count: number | undefined}[] = isSpace
    ? live.patches.map((slice, patch) => ({
        key: `patch-${patch}`,
        count: dropsIn({slice, toneId}),
      }))
    : Array.from({length: ROUND_SECONDS}, (_, index) => {
        const second = seconds.length - (ROUND_SECONDS - 1) + index;
        const slice =
          index === ROUND_SECONDS - 1 ? live.second : seconds[second];
        return {
          key: `second-${second}`,
          count: slice === undefined ? undefined : dropsIn({slice, toneId}),
        };
      });
  const units = isSpace ? 'patches' : 'seconds';

  const changeStrikes = (perMinute: number) => {
    strikesRef.current = perMinute;
    setStrikesPerMinute(perMinute);
    const shower = showerRef.current;
    if (shower) {
      // Waits are memoryless, so the next strike can simply be redrawn.
      shower.nextThunderMs = nextDropAfter({
        ms: performance.now(),
        rate: perMinute / 60,
      });
    }
  };

  const chooseTone = (next: RainTone | undefined) => {
    setIsLightningKnob(false);
    setToneId(next?.id);
    const rate = next ? (rates[next.id] ?? 0) : totalRate;
    const each = isSpace
      ? `${formatRate(patchRate(rate))} drops a patch`
      : `${formatRate(rate)} drops a second`;
    setAnnouncement(
      next
        ? `Counting ${toneNote({tone: next, chord}).name} alone, a voice that follows the chord: ${each} on average.`
        : `Counting every note together: ${each} on average.`,
    );
  };

  /**
   * Sets one note's θ, or, with no note, the θ of all the notes together,
   * scaling each note by the same factor so the mix stays the same. With
   * every note at 0 there is no mix to keep, so the default one is used.
   */
  const changeRate = (changed: RainTone | undefined, rate: number) => {
    const current = ratesRef.current;
    const currentTotal = RAIN_TONES.reduce(
      (sum, each) => sum + (current[each.id] ?? 0),
      0,
    );
    const next: Record<string, number> = {...current};
    const changedTones = changed ? [changed] : RAIN_TONES;
    for (const each of changedTones) {
      const share =
        currentTotal > 0
          ? (current[each.id] ?? 0) / currentTotal
          : each.rate / TOTAL_RATE;
      next[each.id] = changed ? rate : Number((rate * share).toFixed(3));
    }
    ratesRef.current = next;
    setRates(next);
    const shower = showerRef.current;
    if (shower) {
      const nowMs = performance.now();
      // Waits are memoryless, so the next drop can simply be redrawn.
      for (const each of changedTones) {
        shower.nextSpawnMs[RAIN_TONES.indexOf(each)] = nextDropAfter({
          ms: nowMs,
          rate: next[each.id] ?? 0,
        });
      }
      restartCount({shower, nowMs});
    }
    setSeconds([]);
    setPatchSlices([]);
    setLive({second: {}, patches: emptyPatches()});
  };

  const changeVolume = (next: RainVolume) => {
    volumeRef.current = next;
    setVolume(next);
    audioRef.current?.setVolume(next);
    writeRainVolume({volume: next});
  };

  const chooseMode = (next: SliceMode) => {
    setMode(next);
    setAnnouncement(
      next === 'space'
        ? `Counting the drops on each of ${PATCHES} patches of ground, over ${ROUND_SECONDS} seconds: ${formatRate(patchRate(perSecond))} a patch on average.`
        : `Counting the drops in each second: ${formatRate(perSecond)} a second on average.`,
    );
  };

  return createPortal(
    <Layer $isLeaving={isLeaving}>
      <Canvas ref={canvasRef} aria-hidden />
      <RainCafe
        isLeaving={isLeaving}
        leaveMs={LEAVE_MS}
        arriveMs={CLOUDS_IN_MS}
      />
      <RainClouds
        isLeaving={isLeaving}
        skyRef={skyRef}
        cloudBaseRef={cloudBaseRef}
        rollInMs={CLOUDS_IN_MS}
        leaveMs={LEAVE_MS}
      />
      <CloseButton
        ref={closeRef}
        type="button"
        aria-label="Stop the rain"
        title="Stop the rain"
        onClick={() => setIsClosing(true)}
      >
        <FaTimes size={18} aria-hidden />
      </CloseButton>
      <Card role="region" aria-labelledby={titleId}>
        <CardHead>
          <CardTitle id={titleId}>Poisson rain</CardTitle>
          <RoundButton
            type="button"
            aria-expanded={isVolumeOpen}
            aria-controls={volumeId}
            aria-label="Volume"
            title="Volume"
            onClick={() => setIsVolumeOpen(!isVolumeOpen)}
          >
            {volume.rain === 0 && volume.drops === 0 ? (
              <FaVolumeMute aria-hidden />
            ) : (
              <FaVolumeUp aria-hidden />
            )}
          </RoundButton>
          <RoundButton
            type="button"
            aria-expanded={!isFolded}
            aria-controls={chartId}
            aria-label={isFolded ? 'Show the chart' : 'Hide the chart'}
            title={isFolded ? 'Show the chart' : 'Hide the chart'}
            onClick={() => setIsFolded(!isFolded)}
          >
            {isFolded ? (
              <FaChevronUp aria-hidden />
            ) : (
              <FaChevronDown aria-hidden />
            )}
          </RoundButton>
          <RoundButton
            type="button"
            aria-label="Stop the rain"
            title="Stop the rain"
            onClick={() => setIsClosing(true)}
          >
            <FaTimes aria-hidden />
          </RoundButton>
        </CardHead>
        <Volumes
          id={volumeId}
          role="group"
          aria-label="Volume"
          hidden={!isVolumeOpen}
        >
          <label htmlFor={rainVolumeId}>Rain</label>
          <input
            id={rainVolumeId}
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={volume.rain}
            aria-valuetext={`${Math.round(volume.rain * 100)}%`}
            onChange={event =>
              changeVolume({...volume, rain: Number(event.target.value)})
            }
          />
          <label htmlFor={dropsVolumeId}>Drops</label>
          <input
            id={dropsVolumeId}
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={volume.drops}
            aria-valuetext={`${Math.round(volume.drops * 100)}%`}
            onChange={event =>
              changeVolume({...volume, drops: Number(event.target.value)})
            }
          />
        </Volumes>
        <div id={chartId} hidden={isFolded}>
          <Modes role="group" aria-label="Count the drops in">
            <ModeButton
              type="button"
              aria-pressed={!isSpace}
              onClick={() => chooseMode('time')}
            >
              Each second
            </ModeButton>
            <ModeButton
              type="button"
              aria-pressed={isSpace}
              onClick={() => chooseMode('space')}
            >
              Each patch of ground
            </ModeButton>
          </Modes>
          <Lessons>
            <Lesson $isShown={!isSpace} aria-hidden={isSpace}>
              A Poisson counts independent events in a slice of <b>time</b> or
              space. Here the Poisson describes the probability of different
              numbers of drops in one second. It is governed by a single
              parameter, <b>θ</b>.
            </Lesson>
            <Lesson $isShown={isSpace} aria-hidden={!isSpace}>
              A Poisson counts independent events in a slice of time or{' '}
              <b>space</b>. Here the Poisson describes the probability of
              different numbers of drops on one patch in {ROUND_SECONDS}{' '}
              seconds. The ground is cut into {PATCHES} evenly spaced patches.
            </Lesson>
          </Lessons>
          <Strip
            aria-label={
              isSpace
                ? 'Drops on each patch of ground this round, left to right'
                : 'Drops in each of the last ten seconds, the newest last'
            }
          >
            {cells.map(({key, count}, index) => (
              <Cell
                key={key}
                aria-hidden={count === undefined}
                $isEmpty={count === undefined}
                $isFilling={isSpace || index === cells.length - 1}
                $color={barColor}
              >
                {count}
              </Cell>
            ))}
          </Strip>
          <svg viewBox="0 0 300 132" width="100%" aria-hidden>
            <text
              transform={`translate(10 ${(PLOT.top + PLOT.base) / 2}) rotate(-90)`}
              fontSize={10}
              textAnchor="middle"
              fill="#57534e"
            >
              probability
            </text>
            {bins.map(bin => {
              const slot = (PLOT.right - PLOT.left) / bins.length;
              const x = PLOT.left + bin.k * slot;
              const plotHeight = PLOT.base - PLOT.top;
              const barHeight = (bin.observed / tallest) * plotHeight;
              const dotY = PLOT.base - (bin.expected / tallest) * plotHeight;
              return (
                <g key={bin.k}>
                  <rect
                    x={x + slot * 0.15}
                    y={PLOT.base - barHeight}
                    width={slot * 0.7}
                    height={barHeight}
                    rx={2}
                    fill={barColor}
                    opacity={0.75}
                  />
                  <circle cx={x + slot / 2} cy={dotY} r={2.8} fill="#8c1515" />
                  {(bins.length <= 8 || bin.k % 2 === 0) && (
                    <text
                      x={x + slot / 2}
                      y={PLOT.base + 13}
                      fontSize={9}
                      textAnchor="middle"
                      fill="#57534e"
                    >
                      {bin.k}
                    </text>
                  )}
                </g>
              );
            })}
            <path
              d={bins
                .map((bin, index) => {
                  const slot = (PLOT.right - PLOT.left) / bins.length;
                  const x = PLOT.left + bin.k * slot + slot / 2;
                  const y =
                    PLOT.base -
                    (bin.expected / tallest) * (PLOT.base - PLOT.top);
                  return `${index === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
                })
                .join(' ')}
              fill="none"
              stroke="#8c1515"
              strokeWidth={1}
              opacity={0.5}
            />
            <path
              d={`M ${PLOT.left} ${PLOT.top} V ${PLOT.base} H ${PLOT.right}`}
              fill="none"
              stroke="#a8a29e"
              strokeWidth={1}
            />
            <text
              x={(PLOT.left + PLOT.right) / 2}
              y={128}
              fontSize={10}
              textAnchor="middle"
              fill="#57534e"
            >
              {isSpace ? 'drops on one patch' : 'drops in one second'}
            </text>
          </svg>
          <Key>
            <span>
              <Swatch $color={barColor} /> share of {units} with k drops
            </span>
            <span>
              <Dot /> Poisson, θ = {formatRate(lambda)}
            </span>
          </Key>
          <ChipsRow>
            <Chips role="group" aria-label="Which notes to count">
              <Chip
                type="button"
                aria-pressed={toneId === undefined}
                $color="#1c1917"
                onClick={() => chooseTone(undefined)}
              >
                All
              </Chip>
              {RAIN_TONES.map(each => {
                const note = toneNote({tone: each, chord});
                return (
                  <Chip
                    key={each.id}
                    type="button"
                    aria-pressed={toneId === each.id}
                    aria-label={`${note.name}, ${formatRate(rates[each.id] ?? 0)} a second`}
                    $color={each.color}
                    onClick={() => chooseTone(each)}
                  >
                    <Swatch $color={each.color} />
                    <span>
                      <Letter>{note.letter}</Letter>
                      {note.label.slice(note.letter.length)}
                    </span>
                  </Chip>
                );
              })}
            </Chips>
            <Chip
              type="button"
              aria-pressed={isLightningKnob}
              aria-label="Lightning"
              title="Lightning: set its θ"
              $color="#C98A12"
              onClick={() => {
                setIsLightningKnob(true);
                setAnnouncement(
                  `Lightning strikes ${formatRate(strikesPerMinute)} times a minute on average; the slider sets it.`,
                );
              }}
            >
              <FaBolt aria-hidden />
            </Chip>
          </ChipsRow>
          {isLightningKnob ? (
            <Knob $color="#C98A12">
              <label htmlFor={sliderId}>θ for lightning</label>
              <input
                id={sliderId}
                type="range"
                min={0}
                max={MAX_STRIKES_PER_MINUTE}
                step={1}
                value={strikesPerMinute}
                aria-valuetext={`${strikesPerMinute} strikes a minute`}
                onChange={event => changeStrikes(Number(event.target.value))}
              />
              <span aria-hidden>{strikesPerMinute} a minute</span>
            </Knob>
          ) : (
            <Knob $color={tone?.color ?? '#1c1917'}>
              <label htmlFor={sliderId}>
                θ for {tone ? toneNote({tone, chord}).name : 'all notes'}
              </label>
              <input
                id={sliderId}
                type="range"
                min={0}
                max={Math.max(tone ? MAX_NOTE_RATE : MAX_TOTAL_RATE, perSecond)}
                step={0.1}
                value={perSecond}
                aria-valuetext={`${formatRate(perSecond)} drops a second`}
                onChange={event => changeRate(tone, Number(event.target.value))}
              />
              <span aria-hidden>{formatRate(perSecond)} a second</span>
            </Knob>
          )}
          <Credit>Original idea by Kelly Cochran</Credit>
        </div>
      </Card>
      <VisuallyHidden role="status" aria-live="polite">
        {announcement}
      </VisuallyHidden>
    </Layer>,
    document.body,
  );
}

const reducedMotion = css`
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

/** The whole rain, over the page; it never takes a click itself. */
const Layer = styled.div<{$isLeaving: boolean}>`
  position: fixed;
  inset: 0;
  z-index: 10000;
  overflow: hidden;
  pointer-events: none;

  & > canvas,
  & > section {
    animation: ${({$isLeaving}) =>
      $isLeaving
        ? css`
            ${fadeOut} ${LEAVE_MS}ms ease-in forwards
          `
        : css`
            ${fadeIn} 0.8s ease-out ${CLOUDS_IN_MS / 2}ms both
          `};
    ${reducedMotion}
  }
`;

const Canvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border: none;
  border-radius: 50%;
  background: #ffffff;
  color: #1c1917;
  box-shadow: 0 4px 14px rgba(28, 25, 23, 0.25);
  cursor: pointer;
  pointer-events: auto;

  &:hover {
    background: #f5f5f4;
  }

  &:focus-visible {
    outline: 3px solid #1c1917;
    outline-offset: 3px;
  }
`;

const Card = styled.section`
  position: absolute;
  left: 16px;
  bottom: ${GROUND + 12}px;
  width: min(400px, calc(100vw - 32px));
  max-height: calc(100vh - ${GROUND + 140}px);
  overflow-y: auto;
  padding: 12px 14px 10px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 10px 30px rgba(28, 25, 23, 0.25);
  color: #1c1917;
  font-family: 'Source Sans 3', system-ui, sans-serif;
  pointer-events: auto;
`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const CardTitle = styled.h2`
  flex: 1;
  margin: 0;
  font-size: 16px;
  font-weight: 700;
`;

const RoundButton = styled.button`
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border: 1px solid #d6d3d1;
  border-radius: 50%;
  background: #ffffff;
  color: #1c1917;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #1c1917;
    outline-offset: 2px;
  }
`;

/** The rain's and the drops' volume sliders, under the card's buttons. */
const Volumes = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 4px 10px;
  margin-top: 8px;
  padding: 6px 10px;
  border-radius: 12px;
  background: #f5f5f4;
  font-size: 12px;
  color: #44403c;

  &[hidden] {
    display: none;
  }

  label {
    font-weight: 700;
  }

  input {
    width: 100%;
    accent-color: #5a6fd1;
  }
`;

const Modes = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin-top: 10px;
  padding: 3px;
  border-radius: 999px;
  background: #f0eeec;
`;

const ModeButton = styled.button`
  padding: 5px 8px;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: #44403c;
  font: inherit;
  font-size: 13px;
  cursor: pointer;

  &[aria-pressed='true'] {
    background: #ffffff;
    color: #1c1917;
    font-weight: 700;
    box-shadow: 0 1px 4px rgba(28, 25, 23, 0.18);
  }

  &:focus-visible {
    outline: 2px solid #1c1917;
    outline-offset: 2px;
  }
`;

/**
 * Both views' lessons share one grid cell, so the card is always as tall
 * as the longer one and does not jump when the view changes.
 */
const Lessons = styled.div`
  display: grid;
  margin: 8px 0 6px;
`;

const Lesson = styled.p<{$isShown: boolean}>`
  grid-area: 1 / 1;
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: #292524;
  visibility: ${({$isShown}) => ($isShown ? 'visible' : 'hidden')};
`;

const Strip = styled.ol`
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 3px;
  margin: 0 0 4px;
  padding: 0;
  list-style: none;
`;

const Cell = styled.li<{
  $isEmpty: boolean;
  $isFilling: boolean;
  $color: string;
}>`
  display: grid;
  place-items: center;
  height: 24px;
  border-radius: 6px;
  border: 1.5px ${({$isFilling}) => ($isFilling ? 'dashed' : 'solid')}
    ${({$isEmpty, $color}) => ($isEmpty ? '#e7e5e4' : $color)};
  background: ${({$isEmpty}) => ($isEmpty ? 'transparent' : '#ffffff')};
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #1c1917;
`;

const Key = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  font-size: 12px;
  color: #57534e;

  span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
`;

const Swatch = styled.span<{$color: string}>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: ${({$color}) => $color};
`;

const Dot = styled.span`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #8c1515;
`;

/** The note chips and, at the end, the lightning chip, on one row. */
const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;
  margin-top: 8px;
`;

const Chips = styled.div`
  display: contents;
`;

const Chip = styled.button<{$color: string}>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border: 1px solid #d6d3d1;
  border-radius: 999px;
  background: #ffffff;
  color: #1c1917;
  font: inherit;
  font-size: 12px;
  cursor: pointer;

  &[aria-pressed='true'] {
    border-color: ${({$color}) => $color};
    box-shadow: inset 0 0 0 1px ${({$color}) => $color};
    font-weight: 700;
  }

  &:focus-visible {
    outline: 2px solid #1c1917;
    outline-offset: 2px;
  }
`;

/**
 * A chip's note letter, in a slot as wide as the widest letter, so the
 * chips keep their size when the chord changes.
 */
const Letter = styled.span`
  display: inline-block;
  min-width: 0.75em;
  text-align: center;
`;

/** The selected note's θ slider, in that note's colour. */
const Knob = styled.div<{$color: string}>`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 12px;
  color: #44403c;

  label {
    font-weight: 700;
  }

  input {
    width: 100%;
    accent-color: ${({$color}) => $color};
  }

  span {
    min-width: 68px;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
`;

const Credit = styled.p`
  margin: 6px 0 0;
  padding-top: 6px;
  border-top: 1px solid #e7e5e4;
  font-size: 11px;
  color: #6b6560;
`;
